import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CuratedKitCard from "@/components/CuratedKits/CuratedKitCard";
import KitMobileFilters from "@/components/CuratedKits/KitMobileFilters";
import KitFilterModal from "@/components/CuratedKits/KitFilterModal";
import KitsDesktopFilterSidebar from "@/components/CuratedKits/KitsDesktopFilterSidebar"; // Import new sidebar
import { PackageSearch } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
    fetchKits,
    selectAllKits,
    selectKitCurrentPage,
    setCurrentKitPage,
    resetKits,
    selectIsKitLoaded,
    selectKitStatus,
    fetchKitTypes,
    selectKitTypes,
    selectIsKitTypesLoaded,
    selectKitTypesStatus,
} from "../../store/slices/kitsSlice";
import { addToCart } from "../../store/slices/cartSlice";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import EmptyState from "components/EmptyState/EmptyState";
import { titleCase } from "utils/ToTitleCase";
import ExploreSidebarCard from "@/shared/ExploreSidebarCard/ExploreSidebarCard";
import { ShoppingBag } from "lucide-react";

// Hardcoded Budget Filter Options (keeping these as they are not from API)
const BUDGET_FILTERS = [
    { id: "all_budget", label: "All Budgets", value: undefined },
    { id: "under_500", label: "Under ₹500", value: "0-500" },
    { id: "500_1000", label: "₹500 - ₹1000", value: "500-1000" },
    { id: "1000_3000", label: "₹1000 - ₹3000", value: "1000-3000" },
    { id: "3000_5000", label: "₹3000 - ₹5000", value: "3000-5000" },
    { id: "above_5000", label: "Above ₹5000", value: "5000-100000" },
];

const KitsListingPage: FC = () => {
    const PAGE_SIZE = 12; // Increased page size for grid view
    const dispatch = useAppDispatch();
    const kits = useAppSelector(selectAllKits);
    const currentPage = useAppSelector(selectKitCurrentPage);
    const { error, hasReachedEnd, totalCount } = useAppSelector((state) => state.kits);
    const isKitLoaded = useAppSelector(selectIsKitLoaded);
    const kitStatus = useAppSelector(selectKitStatus);

    // Kit Types from API
    const kitTypes = useAppSelector(selectKitTypes);
    const isKitTypesLoaded = useAppSelector(selectIsKitTypesLoaded);
    const kitTypesStatus = useAppSelector(selectKitTypesStatus);

    const [searchParams, setSearchParams] = useSearchParams();

    // State for filtering - Now Arrays
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]);

    // Modal State
    const [activeModal, setActiveModal] = useState<"category" | "budget" | null>(null);

    const isFetchingRef = useRef(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // Initialize filter from URL if present
    // Track the last applied filter key in STATE to avoid race conditions with sessionStorage
    const [lastAppliedFilterKey, setLastAppliedFilterKey] = useState<string>(
        sessionStorage.getItem("lastKitFilterKey") || ""
    );

    // Construct unique filter key
    const filterKey = `${selectedTypes?.sort?.().join(",") || ""}_${selectedBudgets?.sort?.().join(",") || ""}`;

    // Initialize filter from URL or Session if present on MOUNT only
    useEffect(() => {
        // Priority: URL -> Session -> Default
        const typeParam = searchParams.getAll("type");
        const budgetParam = searchParams.getAll("budget");

        if (typeParam.length > 0 || budgetParam.length > 0) {
            if (typeParam.length > 0) setSelectedTypes(typeParam);
            if (budgetParam.length > 0) setSelectedBudgets(budgetParam);
        } else {
            // User requested filters to be cleared on refresh/load if not in URL
        }
    }, []); // Run ONCE on mount

    // Fetch kit types on mount
    useEffect(() => {
        if (!isKitTypesLoaded && kitTypesStatus === "idle") {
            dispatch(fetchKitTypes());
        }
    }, [dispatch, isKitTypesLoaded, kitTypesStatus]);

    // Persist filters to session (optional, keeping for now if user wants to keep state during nav, but not on fresh load?)
    // If we remove restoration, writing to session is less useful but maybe used elsewhere?
    useEffect(() => {
        sessionStorage.setItem("selectedKitTypes", JSON.stringify(selectedTypes || []));
        sessionStorage.setItem("selectedKitBudgets", JSON.stringify(selectedBudgets || []));
    }, [selectedTypes, selectedBudgets]);


    // Update URL and State
    const handleFilterChange = (key: "type" | "budget", values: string[]) => {
        if (!values) values = [];

        const newParams = new URLSearchParams(searchParams);

        // Remove existing
        newParams.delete(key);

        if (key === "type") {
            setSelectedTypes(values);
            values.forEach(v => newParams.append("type", v));
        } else {
            setSelectedBudgets(values);
            values.forEach(v => newParams.append("budget", v));
        }

        setSearchParams(newParams);
    };

    const handleClearAll = () => {
        setSearchParams(new URLSearchParams());
        setSelectedTypes([]);
        setSelectedBudgets([]);
    };

    const fetchKitsWithFilters = useCallback(
        (page: number) => {
            if (isFetchingRef.current || (hasReachedEnd && page > 1)) return;
            isFetchingRef.current = true;

            let minPrice: number | undefined;
            let maxPrice: number | undefined;

            if (selectedBudgets && selectedBudgets.length > 0) {
                const [min, max] = selectedBudgets[0].split("-").map(Number);
                if (!isNaN(min)) minPrice = min;
                if (!isNaN(max)) maxPrice = max;
            }

            const filters = {
                page,
                pageSize: PAGE_SIZE,
                type: selectedTypes && selectedTypes.length > 0 ? selectedTypes : undefined,
                minPrice,
                maxPrice
            };

            dispatch(fetchKits(filters)).finally(() => {
                isFetchingRef.current = false;
            });
        },
        [dispatch, selectedTypes, selectedBudgets, hasReachedEnd]
    );

    // Filter Change Effect
    useEffect(() => {
        const filtersChanged = filterKey !== lastAppliedFilterKey;

        // We use totalCount > 0 as a proxy to know if we have fetched a list before.
        const hasListData = kits.length > 0 && totalCount > 0;

        if (!filtersChanged && hasListData) {
            return;
        }

        // If filters changed, OR we don't have list data (e.g. fresh load or only detail data), FETCH.
        if (filtersChanged || !hasListData) {
            // Commit to the new key immediately to prevent loops
            setLastAppliedFilterKey(filterKey);
            sessionStorage.setItem("lastKitFilterKey", filterKey);

            // Only reset if filters actually changed (to avoid wiping data on a mere refresh if keys match)
            // But if !hasListData, we might want to reset potential partial data? 
            // Safer to reset if filters changed.
            if (filtersChanged) {
                dispatch(resetKits());
                dispatch(setCurrentKitPage(1));
            }

            fetchKitsWithFilters(1);
        }
    }, [filterKey, lastAppliedFilterKey, fetchKitsWithFilters, totalCount, dispatch, kits.length]);

    // Handle Pagination
    useEffect(() => {
        if (currentPage > 1) {
            fetchKitsWithFilters(currentPage);
        }
    }, [currentPage, fetchKitsWithFilters]);

    // Infinite Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isFetchingRef.current && !hasReachedEnd && kitStatus === 'succeeded') {
                    dispatch(setCurrentKitPage(currentPage + 1));
                }
            },
            { threshold: 0.1 }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }
        observerRef.current = observer;

        return () => observer.disconnect();
    }, [currentPage, hasReachedEnd, kitStatus, dispatch]);

    const handleMobileApply = (key: "type" | "budget", value: string | undefined) => {
        if (value) handleFilterChange(key, [value]);
        else handleFilterChange(key, []);
    };
    // Adapter for KitMobileFilters
    const mobileSelectedType = selectedTypes && selectedTypes.length > 0 ? selectedTypes[0] : undefined; // Just take first
    const mobileSelectedBudget = selectedBudgets && selectedBudgets.length > 0 ? selectedBudgets[0] : undefined;

    // Create dynamic KIT_FILTERS from fetched types
    const KIT_FILTERS = [
        { id: "all", label: "All Kits", value: undefined },
        ...kitTypes.map((type) => ({
            id: type.id,
            label: titleCase(type.name),
            value: type.id, // Use id as value for consistency
        })),
    ];
    
    return (
        <div className="min-h-[60vh]">
            <Helmet>
                <title>Curated Kits || 360CUSTOMIZER</title>
            </Helmet>

      <div className="container min-h[200vh] pt-4 space-y-16 sm:space-y-20 lg:space-y-12">
        <main>

            {/* Mobile Filters Bar */}
            <KitMobileFilters
                selectedType={mobileSelectedType}
                selectedBudget={mobileSelectedBudget}
                onOpenTypeModal={() => setActiveModal("category")}
                onOpenBudgetModal={() => setActiveModal("budget")}
                onClear={() => {
                    handleFilterChange("type", []);
                    handleFilterChange("budget", []);
                }}
            />

            {/* Explore All Products Card for Mobile */}
            <div className="lg:hidden px-4 mt-2">
                <ExploreSidebarCard to="/products" icon={ShoppingBag} text="Explore All Products" />
            </div>

            {/* Mobile Filter Modal - Keeping single select for now as per existing UI, or update if easy */}
            <KitFilterModal
                isOpen={!!activeModal}
                onClose={() => setActiveModal(null)}
                title={activeModal === "category" ? "Category" : "Budget"}
                selectedType={activeModal === "category" ? mobileSelectedType : mobileSelectedBudget}
                onApply={(value) => handleMobileApply(activeModal === "category" ? "type" : "budget", value)}
                filterOptions={activeModal === "category" ? KIT_FILTERS : BUDGET_FILTERS}
            />

            <div className="container mx-auto px-0 py-4">
                <div className="flex flex-col lg:flex-row">
                    {/* Desktop Sidebar with Checkboxes */}
                    <aside className="hidden lg:block lg:w-1/5 xl:w-1/5 flex-shrink-0">
                        <KitsDesktopFilterSidebar
                            selectedTypes={selectedTypes || []}
                            selectedBudgets={selectedBudgets || []}
                            onTypeChange={(values) => handleFilterChange("type", values)}
                            onBudgetChange={(values) => handleFilterChange("budget", values)}
                            showClear={selectedTypes.length > 0 || selectedBudgets.length > 0}
                            onClear={handleClearAll}
                            filters={{
                                types: KIT_FILTERS,
                                budgets: BUDGET_FILTERS
                            }}
                        />
                    </aside>

                    {/* Vertical Separator */}
                    <div className="flex-shrink-0 mb-10 lg:mb-0 lg:mx-4">
                        <div className="h-full border-r border-[#e2e1e1]"></div>
                    </div>

                    {/* Main Content */}
                    <main className="flex-1">
                        <div className="mb-6 flex items-center justify-between">
                            <h1 className="text-2xl font-bold text-gray-900">
                                {selectedTypes && selectedTypes.length > 0
                                    ? KIT_FILTERS.find(f => f.value === selectedTypes[0])?.label || "Curated Kits"
                                    : "Curated Kits"}
                                <span className="text-gray-500 font-normal text-base ml-3">
                                    ({totalCount} Items)
                                </span>
                            </h1>
                        </div>

                        {/* Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                            {kits.map((kit) => (
                                <CuratedKitCard
                                    key={kit.id}
                                    data={kit}
                                    dispatch={dispatch}
                                    addToCart={addToCart as any}
                                />
                            ))}
                        </div>

                        {/* Loading State */}
                        {kitStatus === "loading" && (
                            <div className="flex justify-center items-center sm:mt-40 sm:ml-20">
                                <img src={infiniteLoader} alt="loader" className="w-1/5" />
                            </div>
                        )}

                        {/* Error State */}
                        {kitStatus === "failed" && (
                            <div className="py-10">
                                <ErrorFallback
                                    message={error || "Failed to load kits"}
                                    onRetry={handleClearAll}
                                />
                            </div>
                        )}

                        {/* Empty State */}
                        {kits.length === 0 && kitStatus === "succeeded" && (
                            <div className="py-20">
                                <EmptyState
                                    icon={PackageSearch}
                                    title="No Kits Found"
                                    message="Try ensuring you have selected the correct category."
                                    buttonLabel="View All Kits"
                                    onButtonClick={handleClearAll}
                                />
                            </div>
                        )}

                        {/* Sentinel for infinite scroll */}
                        <div ref={sentinelRef} className="h-4 w-full" />
                    </main>
                </div>
            </div>
        </main>
        </div>
        </div>
    );
};

export default KitsListingPage;
